import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/FormField";
import { Input } from "@/components/ui/Input";
import { ApiError } from "@/lib/api";

import { useAuth } from "./AuthContext";

// Phase 2a customer-accounts FR-3 — standalone signup. The inline
// version inside checkout (see 2c) shares the same /api/v1/auth/signup
// endpoint but lives in a different component; this page exists for
// users who want to create an account before browsing.
export function SignUpPage(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
    password_confirmation: "",
    first_name: "",
    last_name: "",
    phone: ""
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);

  const onChange = (key: keyof typeof form) => (e: { target: { value: string } }) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    setTopError(null);
    try {
      await auth.signUp({
        ...form,
        email: form.email.trim().toLowerCase(),
        password_confirmation: form.password
      });
      navigate("/account", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as { errors?: Record<string, string> } | undefined;
        const fieldErrors = body?.errors ?? {};
        // The server prefixes profile fields with `profile_` (see
        // SignupsController#error_messages); fold them back so the UI
        // tags them next to the right input.
        const mapped: Record<string, string> = {};
        Object.entries(fieldErrors).forEach(([k, v]) => {
          const key = k.startsWith("profile_") ? k.slice("profile_".length) : k;
          mapped[key] = v;
        });
        setErrors(mapped);
      } else if (err instanceof ApiError && err.status === 429) {
        setTopError("Too many signup attempts from this network. Try again in a few minutes.");
      } else {
        setTopError("Something went wrong creating your account. Try again.");
      }
    }
  };

  return (
    <section className="mx-auto max-w-md space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-brand-black">Create your account</h1>
        <p className="mt-1 text-sm text-zinc-600">
          Already have one?{" "}
          <Link to="/sign-in" className="text-brand-red underline">
            Sign in
          </Link>
          .
        </p>
      </header>

      <form onSubmit={onSubmit} noValidate className="space-y-4 rounded-md border border-zinc-200 bg-brand-white p-5">
        {topError ? (
          <Alert variant="destructive" role="alert">
            {topError}
          </Alert>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <FormField id="first_name" label="First name" required error={errors.first_name}>
            <Input id="first_name" value={form.first_name} onChange={onChange("first_name")} autoComplete="given-name" required />
          </FormField>
          <FormField id="last_name" label="Last name" required error={errors.last_name}>
            <Input id="last_name" value={form.last_name} onChange={onChange("last_name")} autoComplete="family-name" required />
          </FormField>
        </div>

        <FormField id="email" label="Email" required error={errors.email}>
          <Input id="email" type="email" value={form.email} onChange={onChange("email")} autoComplete="email" required />
        </FormField>

        <FormField id="phone" label="Phone" required hint="Include country code, e.g. +1 720-555-1234" error={errors.phone}>
          <Input id="phone" type="tel" value={form.phone} onChange={onChange("phone")} autoComplete="tel" required />
        </FormField>

        <FormField id="password" label="Password" required hint="At least 10 characters, with a letter and a number." error={errors.password}>
          <Input id="password" type="password" value={form.password} onChange={onChange("password")} autoComplete="new-password" required />
        </FormField>

        <Button type="submit" disabled={auth.loading} className="w-full">
          {auth.loading ? "Creating your account…" : "Create account"}
        </Button>
      </form>
    </section>
  );
}
